import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { IWorkspaceDirectoryItem } from '@platform/dao';
import { useMeQuery, useRequestAccessMutation, useWorkspaceDirectoryQuery } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import { Icon } from '@platform/ui';
import { useLabel } from '@platform/hooks/useLabel';
import { cx } from '@platform/core/lib/cx';
import { workspaceType, workspaceTypeOrder } from '@platform/navigation';
import { memberWorkspaces } from '../memberWorkspaces';
import { prototypeWorkspaceTypes, workspaceGroup } from '../workspaceGroup';
import { trapTab } from '@platform/ui/focusTrap';
import type { IWorkspaceNavigatorProps } from './IWorkspaceNavigatorProps';
import { OtherWorkspaceCard } from './OtherWorkspaceCard';
import { WorkspaceCard } from './WorkspaceCard';
import '../navigator/navigator.css';
import './wsnav.css';

/**
 * Навігатор робочих просторів («Показати всі робочі простори…», прототип v4 G:L3600–3672): пошук «Шукати робочі простори» з «N з M»,
 * чипи типів («Усі · N», «Комітети · N»…), «Ваші робочі простори · N» — картки з переходом; «Інші робочі простори, до яких можна
 * попроситися · N» — з каталогу платформи (без каталогу розділу немає), «Запросити доступ» / «Доступ запитано». Esc, клік по тлу й ×
 * закривають; Enter відкриває перший збіг.
 */
export function WorkspaceNavigator({ ws, onOpen, onClose }: IWorkspaceNavigatorProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { data: me } = useMeQuery();
  const { data: dir } = useWorkspaceDirectoryQuery();
  const [requestAccess] = useRequestAccessMutation();
  const dispatch = useAppDispatch();
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => previous?.focus?.();
  }, []);
  const mine = memberWorkspaces(me?.workspaces ?? []);
  const others = dir?.available ? dir.items.filter((w) => !w.myRole) : [];
  const all: Array<{ type: string; name: string }> = [...mine, ...others];
  const typeOf = (w: { type: string }) => workspaceGroup(w.type);
  const match = (w: { type: string; name: string }) => (!type || typeOf(w) === type) && w.name.toLowerCase().includes(q.trim().toLowerCase());
  const shown = mine.filter(match);
  const shownOthers = others.filter(match);
  const types = workspaceTypeOrder().filter((k) => prototypeWorkspaceTypes.includes(k) || all.some((w) => typeOf(w) === k));
  const request = (w: IWorkspaceDirectoryItem) => void requestAccess({ ws: w.id }).unwrap()
    .then(() => dispatch(toastShown({ kind: 'success', text: t('wsNavigator.requestSent', { name: w.name }) })))
    .catch(() => dispatch(toastShown({ kind: 'error', text: t('wsNavigator.requestFailed') })));
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab' && box.current) trapTab(box.current, e.nativeEvent);
    else if (e.key === 'Enter' && document.activeElement === input.current && shown[0]) onOpen(shown[0].id);
  };
  const chip = (key: string, text: string, count: number) => (
    <button key={key || 'all'} type="button" className={cx('wn-chip', type === key && 'is-on')} aria-pressed={type === key} onClick={() => setType(key)}>{text} · {count}</button>
  );
  return (
    <div className="nv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={box} className="nv-dialog wn-dialog" role="dialog" aria-modal="true" aria-label={t('wsNavigator.title')} onKeyDown={onKeyDown}>
        <div className="nv-head">
          <label className="nv-search">
            <Icon name="search" size={16} strokeWidth={2} />
            <input ref={input} value={q} placeholder={t('wsNavigator.search')} aria-label={t('wsNavigator.search')} onChange={(e) => setQ(e.target.value)} />
            <span className="nv-search__count">{t('allModules.shown', { shown: shown.length + shownOthers.length, count: all.length })}</span>
          </label>
          <button type="button" className="nv-close" aria-label={t('navigator.close')} title={t('navigator.closeTitle')} onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="wn-chips">
          {chip('', t('wsNavigator.all'), all.length)}
          {types.map((k) => chip(k, label(workspaceType(k).plural), all.filter((w) => typeOf(w) === k).length))}
        </div>
        <div className="wn-body">
          {shown.length > 0 && (
            <div className="wn-group">
              <span className="am-section__label">{t('wsNavigator.mine', { count: shown.length })}</span>
              <div className="wn-grid">{shown.map((w) => <WorkspaceCard key={w.id} workspace={w} current={w.id === ws} onOpen={onOpen} />)}</div>
            </div>
          )}
          {shownOthers.length > 0 && (
            <div className="wn-group">
              <span className="am-section__label">{t('wsNavigator.others', { count: shownOthers.length })}</span>
              <div className="wn-grid">{shownOthers.map((w) => <OtherWorkspaceCard key={w.id} workspace={w} onRequest={request} />)}</div>
            </div>
          )}
          {!shown.length && !shownOthers.length && <div className="am-empty">{t('wsNavigator.empty', { q })}</div>}
        </div>
      </div>
    </div>
  );
}
